import _ from "lodash";
import React, { useContext, useEffect, useState, useRef } from "react";
import { Button, Card, CardBody, Col, Input, Table, Row } from "reactstrap";
import axios from "axios";
import Select from "react-select";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, helpers, UserContext } from "../utils";
import { ItemTypeahead, ResaleItemTypeahead, SpinnerButton, TestItemTypeahead } from "./";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { useGetItemInfo, useGetInstronInvalidSpecsLogged } from "../hooks";

const itemEmpty = { selectedItem: { label: "--- Select ---", value: "" } };
const RadioButttonTypes = { XLS: true, PDF: false };

export default function EnterQualityTestData() {
   const { currentUser } = useContext(UserContext);
   const cancelToken = axios.CancelToken;
   const FirstRender = useRef(true);
   const FirstResaleRender = useRef(true);
   const list = useGetInstronInvalidSpecsLogged();
   const navigate = useNavigate();
   const ReportItemFirstRender = useRef(true);
   const source = cancelToken.source();
   const [canCopy, setCanCopy] = useState(false);
   const [editTestItem, setEditTestItem] = useState(itemEmpty);
   const [isLoading, setIsLoading] = useState(false);
   const [newTestItem, setNewTestItem] = useState(null);
   const [onCopy, setOnCopy] = useState(false);
   const [reportingTestDates, setReportingTestDates] = useState(null);
   const [reportItem, setReportItem] = useState(itemEmpty);
   const [resaleDto, setResaleDto] = useState(null);
   const [resaleItem, setResaleItem] = useState(itemEmpty);
   const [resaleTestDates, setResaleTestDates] = useState(null);
   const [screenForm, setScreenForm] = useState({
      payload: {
         id: 0,    // This is being used for the confidence percent
         itemNumber: "",
         isExcel: true,
         endDate: "",
         startDate: "",
      },
   });
   const [selectedResaleDate, setSelectedResaleDate] = useState(null);
   const [selectedTestDate, setSelectedTestDate] = useState(null);
   const [showBluehill, setShowBluehill] = useState(false);        // Bluehill QC Test Transfers
   const [showCopy, setShowCopy] = useState(false);                // Copy Test for Buy Resell
   const [showReport, setShowRpt] = useState(false);               // Quality Test Report
   const [showTests, setShowTests] = useState(true);               // Edit or New
   const [testDates, setTestDates] = useState(null);
   const [zLevel, setZLevel] = useState(95);
   const zLevelMin = 50;
   const zLevelMax = 100;
   const itemInfo = useGetItemInfo(editTestItem);

   useEffect(() => {
      if (!currentUser) return;
      if (currentUser.isAdmin || currentUser.isPowerUser || currentUser.isQualityEdit) {
         setCanCopy(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser])

   useEffect(() => {
      if (editTestItem.selectedItem === null || "") {
         setSelectedTestDate(null);
      }
      if (FirstRender.current) {
         FirstRender.current = false;
         return;
      }
      fetchTestDates();
      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [editTestItem.selectedItem]);

   useEffect(() => {
      if (ReportItemFirstRender.current) {
         ReportItemFirstRender.current = false;
         return;
      }
      fetchTestDates(true);
      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [reportItem.selectedItem]);

   useEffect(() => {
      setResaleDto(null);
      setResaleTestDates(null);

      if (resaleItem.selectedItem === null || "") {
         setSelectedResaleDate(null);
      }
      if (FirstResaleRender.current) {
         FirstResaleRender.current = false;
         return;
      }
      fetchResaleItemDates();
      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [resaleItem.selectedItem]);

   const addNewTest = () => {
      if (!newTestItem?.value) {
         toast.error("Please select an item first");
         return;
      }
      navigate(constants.ENDPOINTS.editTestItem, {
         state: { ItemNumber: newTestItem?.value },
      });
   };

   function fetchResaleItemDates() {
      let specNumber = "";
      if (resaleItem.selectedItem && resaleItem.selectedItem.value) {
         specNumber = resaleItem.selectedItem.value;
      } else {
         toast.info("Please select an item to display test dates.");
         return;
      }

      return api
         .post(`Quality/GetResaleItemTestDates/${specNumber}`)
         .then((r) => {
            if (r.data.success) {
               setResaleDto(r.data.item);
               let testDates = r.data.testDates;
               let selectList = _.map(testDates, (t) => {
                  t["label"] = dateHelpers.toLongDateString(t.testDate);
                  t["value"] = t.testID;
                  t["epoch"] = Date.parse(t.testDate);
                  return t;
               });
               selectList = _.orderBy(selectList, "epoch", "desc");
               setResaleTestDates(selectList);
            } else {
               toast.error(`Failed to get the test dates for: ${specNumber}`);
            }
         })
         .catch(api.catchHandler);
   };

   const fetchTestDates = (reporting) => {
      let specNumber = "";
      if (reporting) {
         if (reportItem.selectedItem && reportItem.selectedItem.value) {
            specNumber = reportItem.selectedItem.value;
         } else {
            toast.info("Please select an item to display test dates.");
            return;
         }
      } else {
         if (editTestItem.selectedItem && editTestItem.selectedItem.value) {
            specNumber = editTestItem.selectedItem.value;
         } else {
            toast.info("Please select an item to display test dates.");
            return;
         }
      }
      return api
         .post(`Quality/GetTestDates/${specNumber}`)
         .then((r) => {
            if (r.data.success) {
               let testDates = r.data.testDates;
               let selectList = _.map(testDates, (t) => {
                  t["label"] = dateHelpers.toLongDateString(t.testDate);
                  t["value"] = t.testID;
                  t["epoch"] = Date.parse(t.testDate);
                  return t;
               });
               selectList = _.orderBy(selectList, "epoch", "desc");
               reporting
                  ? setReportingTestDates(selectList)
                  : setTestDates(selectList);
            } else {
               toast.error(`Failed to get the test dates for: ${specNumber}`);
            }
         })
         .catch(api.catchHandler);
   };

   function handleZValChange(value) {
      let intVal = parseInt(value, 10);
      if (isNaN(intVal)) {
         setZLevel(zLevelMin);
      } else {
         // Clamp to range
         if (intVal < zLevelMin) intVal = zLevelMin;
         if (intVal > zLevelMax) intVal = zLevelMax;
         setZLevel(intVal);
      }
   }

   const onChange = (property, value, copy = null) => {
      if (copy === null) copy = _.cloneDeep(screenForm);
      _.set(copy, property, value);
      setScreenForm(copy);
      return;
   };

   function onCopyTest() {
      if (!resaleDto || _.isNil(resaleDto.item)) {
         toast.info("Please select a valid item");
         return;
      }

      if (!selectedResaleDate) {
         toast.info("Please select a valid test date");
         return;
      }

      setOnCopy(true);
      api
         .post(`Quality/CopyYarnTestToResaleItem/${resaleDto.item}/${selectedResaleDate.value}`)
         .then((response) => {
            if (response.data.success) {
               toast.success("The selected yarn item test was successfully copied to the resale item");
               setResaleItem(itemEmpty);
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(setOnCopy(false));
   }

   const onDeleteTest = () => {
      confirmAlert({
         title: "Permanently Delete Test?",
         message: `This will permanently delete this test. Are you sure?`,
         buttons: [
            {
               label: "Ok",
               onClick: () => {
                  api
                     .post(`Quality/DeleteTestData/${selectedTestDate.value}`)
                     .then((response) => {
                        if (response.data.success) {
                           toast.success(response.data.message);
                           fetchTestDates(reportItem.selectedItem.value);
                           setSelectedTestDate(null);
                        } else {
                           toast.error(response.data.message);
                        }
                     })
                     .catch(api.catchHandler);
               },
            },
            {
               label: "Cancel",
            },
         ],
      });
   };

   const onEditTest = () => {
      // before this can even be called
      // check to ensure the item has a valid specNumber
      if (
         !itemInfo.success ||
         _.isNil(itemInfo.item) ||
         _.isEmpty(itemInfo.item?.specNumber)
      ) {
         toast.info("Please select a valid item");
         return;
      }
      const payload = {
         SpecNumber: itemInfo.item?.specNumber,
         TestId: selectedTestDate.value,
      };
      if (!editTestItem.selectedItem) return;
      navigate(constants.ENDPOINTS.editTestItem, { state: payload });
   };

   const onSelectChange = (opt) => {
      if (opt && opt.value) {
         setSelectedTestDate(opt);
      } else {
         toast.info("The selection is empty or is missing a value property");
      }
   };

   const onSelectChangeResale = (opt) => {
      if (opt && opt.value) {
         setSelectedResaleDate(opt);
      } else {
         toast.info("The selection is empty or is missing a value property");
      }
   };

   const processTransferTest = () => {
      setIsLoading(true);
      api
         .fetchS(`Quality/BlueHillTransferTests`, {}, source.token)
         .then((response) => {
            if (response && response.data) {
               if (response.data.success === false || response.data.result === 0) {
                  throw new Error(response.data.message);
               } else {
                  toast.success("SQL Agent Job Has Completed");
               }
            }
         })
         .catch((error) => {
            toast.error("SQL Agent Job failed, please contact your admin");
            api.catchHandler(error);
         })
         .finally(() => setIsLoading(false));
   };

   function ResetButtons(val) {
      if ((showBluehill && val !== "showBluehill")
         || (!showBluehill && val === "showBluehill"))
         setShowBluehill(!showBluehill);

      if ((showCopy && val !== "showCopy")
         || (!showCopy && val === "showCopy"))
         setShowCopy(!showCopy);

      if ((showReport && val !== "showReport")
         || (!showReport && val === "showReport"))
         setShowRpt(!showReport);

      if ((showTests && val !== "showTests")
         || (!showTests && val === "showTests"))
         setShowTests(!showTests);
   }

   const SetEditTestItemInfo = (opt) => {
      if (_.isNil(opt)) {
         toast.info("Please select a valid item");
      }
      setEditTestItem({ selectedItem: opt });
   };

   const SetReportItemInfo = (opt) => {
      if (_.isNil(opt)) {
         toast.info("Please select a valid item");
      }
      setReportItem({ selectedItem: opt });
      if (reportItem.selectedItem !== opt) {
         setScreenForm({
            ...screenForm,
            payload: { ...screenForm.payload, endDate: "", startDate: "" },
         });
      }
   };

   const SetResaleItemInfo = (opt) => {
      if (_.isNil(opt)) {
         toast.info("Please select a valid item");
      }
      setResaleItem({ selectedItem: opt });
   };

   const TestingReport = () => {
      if (!reportItem.selectedItem.value) return;
      let payload = Object.assign({}, screenForm.payload);
      payload["itemNumber"] = reportItem.selectedItem.value;
      payload["id"] = zLevel;
      if (!payload.endDate || !payload.startDate) {
         toast.error("The start and end dates must be selected");
         return;
      } else {
         if (payload.startDate.epoch > payload.endDate.epoch) {
            toast.error("The start date cannot be greater than the end date");
            return;
         }
         payload.startDate = dateHelpers.formatDateForServer(payload.startDate.epoch);
         payload.endDate = dateHelpers.formatDateForServer(payload.endDate.epoch);
      }
      setIsLoading(true);
      api
         .post("Reference/SpecificationExist", {
            itemNumber: payload.itemNumber,
         })
         .then((r) => {
            if (r && r.data && r.data.exist) {
               if (payload.isExcel) {
                  api.postGetBlob(`Report/QualityTest`, payload)
                     .then((response) => {
                        if (response && response.data) {
                           if (response.data.success === false) {
                              if (_.isString(response?.message)) {
                                 toast.error(response?.message);
                              }
                              return;
                           }
                           let XLS_FILENAME = `Test Results ${payload.itemNumber} ${dateHelpers.toLongDateString(dateHelpers.monthDayAndYear(Date.now()))}.xlsx`;
                           helpers.downloadExport(
                              response.data,
                              XLS_FILENAME,
                              constants.MIME_XLSX
                           );
                        }
                        else {
                           if (_.isString(response?.message))
                              toast.error(response?.message);
                        }
                     })
                     .catch((error) => {
                        api.catchHandler(error);
                        toast.error(error);
                     });
               } else {
                  api
                     .postDownloadBinary(
                        `Report/QualityTest`,
                        payload,
                        constants.MIME_TYPES.pdf,
                        `Test Results ${payload.itemNumber} ${dateHelpers.toLongDateString(dateHelpers.monthDayAndYear(Date.now()))}.pdf`
                     )
                     .then((res) => {
                        if (res?.data === false) {
                           toast.error(res.message);
                        }
                     })
                     .catch((error) => {
                        api.catchHandler(error);
                        toast.error(error);
                     });
               }
            } else {
               toast.info(`No Specification found for ${payload.itemNumber}`);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error("There was an error downloading the quality test report");
         })
         .finally(() => setIsLoading(false));
   };

   const TransferTest = () => {
      confirmAlert({
         title: "Confirm Moving Test",
         message: `Please make sure no one is working with tests in 
        Bluehill before running this job. 
        Are you sure you want to transfer tests from Bluehill?`,
         buttons: [
            {
               label: "Yes",
               onClick: () => {
                  processTransferTest();
               },
            },
            {
               label: "No",
            },
         ],
      });
   };

   return (
      document.title = `Quality Tests Main`,
      <DesktopContainer className="mt-3" screenName={"Quality Tests Main"}>
         {/* Tab Buttons */}
         <div className="filter-header stick-top mb-3">
            <div className="dashboard-button-container">
               <Button
                  children={"Quality Tests"}
                  className="filter-button"
                  onClick={() => ResetButtons("showTests")}
                  active={showTests}
               />
               <Button
                  children={"Bluehill Transfers"}
                  className="filter-button"
                  onClick={() => ResetButtons("showBluehill")}
                  active={showBluehill}
               />
               <Button
                  children={"Reports"}
                  className="filter-button"
                  onClick={() => ResetButtons("showReport")}
                  active={showReport}
               />
               {canCopy ? (
                  <Button
                     children={"Copy Test"}
                     className="filter-button"
                     onClick={() => ResetButtons("showCopy")}
                     active={showCopy}
                  />
               ) : (false)}
            </div>
         </div>

         {/* Edit Test */}
         {showTests ? (
            <Card className="dashboard-card" style={{ minWidth: '960px' }}>
               <CardBody>
                  <div><span className="h2 me-2">Edit Test</span></div>
                  <hr />
                  <Row className="mt-3">
                     <Col xs="3" className="centerRight">
                        Item:
                     </Col>
                     <Col xs="6">
                        <TestItemTypeahead
                           value={editTestItem.selectedItem}
                           isTestItem
                           onChange={(opt) => SetEditTestItemInfo(opt)}
                        />
                     </Col>
                  </Row>

                  {editTestItem?.selectedItem?.value.length > 0 ? (
                     <Row className="mt-3">
                        <Col xs="3" className="centerRight">
                           Test Date:
                        </Col>
                        <Col xs="6">
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              options={editTestItem.selectedItem ? testDates : null}
                              value={editTestItem.selectedItem ? selectedTestDate : null}
                              onChange={(opt) => onSelectChange(opt)}
                              onSelectChange={() => setSelectedTestDate(null)}
                           />
                        </Col>
                     </Row>
                  ): (false)}

                  {testDates ? (
                     <Row className="mt-3 mb-5">
                        <Col xs="3" />
                        <Col>
                           <Button
                              className="shredButton"
                              style={{ marginRight: "15px" }}
                              onClick={() => onEditTest()}
                              disabled={!selectedTestDate}
                           >EDIT TEST</Button>

                           <Button
                              className="shredButton"
                              style={{ marginRight: "15px" }}
                              onClick={() => onDeleteTest()}
                              disabled={!selectedTestDate}
                           >DELETE TEST</Button>
                        </Col>
                     </Row>
                  ) : (false)}
               </CardBody>
            </Card>
         ) : (false)}

         {/* Add New Test */}
         {showTests ? (
            <Card className="dashboard-card" style={{ minWidth: '960px' }}>
               <CardBody>
                  <div><span className="h2 me-2">New Test</span></div>
                  <hr />
                  <Row className="mt-3 mb-5">
                     <Col xs="3" className="centerRight">
                        Item:
                     </Col>
                     <Col xs="6">
                        <ItemTypeahead
                           value={newTestItem}
                           onChange={(opt) => setNewTestItem(opt)}
                           filterItemBy={constants.TYPE_AHEAD_CLASSES.SpecItemsWithAllActiveSamples}
                        />
                     </Col>
                     <Col>
                        <Button
                           className="shredButton"
                           onClick={addNewTest}
                           disabled={isLoading}
                        >ADD NEW TEST</Button>
                     </Col>
                  </Row>
               </CardBody>
            </Card>
         ) : (false)}

         {/* Bluehill Test Transfer */}
         {showBluehill ? (
            <Card className="dashboard-card" style={{ minWidth: '960px' }}>
               <CardBody>
                  <div><span className="h2 me-2">Bluehill QC Test Transfers</span></div>
                  <hr />
                  <Row className="mt-3">
                     <Col xs="3" className="centerRight" />
                     <Col xs="6">
                        <Button
                           className="shredButton"
                           onClick={() => TransferTest()}
                           disabled={isLoading}
                        >TRANSFER TESTS</Button>
                     </Col>
                  </Row>
                  <Row className="mt-4 mb-5">
                     <Col xxl={{ size: 6, offset: 3 }}>
                        {_.isEmpty(list) ? (
                           <span children={<h5>No Instron Tests were found</h5>} />
                        ) : (
                           <Table size="sm" className="data-table p-5">
                              <thead className="table-header">
                                 <tr>
                                    {/* <th>Log ID</th> hidden until...*/}
                                    <th>Log Date</th>
                                    <th>Invalid Specs</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {_.map(list, (e, index) => {
                                    return (
                                       <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                          {/* <td>{e.logId}</td> */}
                                          <td className="data-cell">{dateHelpers.toLongDateString(e.logDate)}</td>
                                          <td className="data-cell">{e.moreInfo}</td>
                                       </tr>
                                    );
                                 })}
                              </tbody>
                           </Table>
                        )}
                     </Col>
                  </Row>
               </CardBody>
            </Card>
         ) : (false)}

         {/* Copy PM Test to Buy Resell Item */}
         {showCopy ? (
            <Card className="dashboard-card" style={{ minWidth: '960px' }}>
               <CardBody>
                  <div><span className="h2 me-2">Copy Test for Buy Resell</span></div>
                  <hr />
                  <Row className="mt-3">
                     <Col xs="3" className="centerRight">
                        Item:
                     </Col>
                     <Col xs="6">
                        <ResaleItemTypeahead
                           value={resaleItem.selectedItem}
                           onChange={(opt) => SetResaleItemInfo(opt)}
                        />
                     </Col>
                  </Row>
                  <br />

                  {resaleDto ? (
                     <Row>
                        <Col xs="3" />
                        <Col>Copy selected test from yarn item {resaleDto.component} to resale item {resaleDto.item}</Col>
                     </Row>
                  ): (false)}

                  {resaleTestDates ? (
                     <Row className="mt-3">
                        <Col xs="3" className="centerRight">
                           Test Date:
                        </Col>
                        <Col xs="6">
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              options={resaleItem.selectedItem ? resaleTestDates : null}
                              value={resaleItem.selectedItem ? selectedResaleDate : null}
                              onChange={(opt) => onSelectChangeResale(opt)}
                              onSelectChange={() => setSelectedResaleDate(null)}
                           />
                        </Col>
                     </Row>
                  ) : (false)}

                  {resaleTestDates ? (
                     <Row className="mt-3 mb-5">
                        <Col xs="3" />
                        <Col>
                           <SpinnerButton
                              className="shredButton"
                              style={{ marginRight: "15px" }}
                              onClick={() => onCopyTest()}
                              on={onCopy}
                              disabled={onCopy || !selectedResaleDate}
                           >COPY TEST</SpinnerButton>
                        </Col>
                     </Row>
                  ) : (false)}

               </CardBody>
            </Card>
         ) : (false)}

         {/* QC Test Report */}
         {showReport ? (
            <Card className="dashboard-card" style={{ minWidth: '960px' }}>
               <CardBody>
                  <div><span className="h2 me-2">Quality Test Report</span></div>
                  <hr />
                  <Row className="mt-3">
                     <Col xs="3" className="centerRight">
                        Item:
                     </Col>
                     <Col xs="6">
                        <TestItemTypeahead
                           value={reportItem.selectedItem}
                           onChange={(opt) => SetReportItemInfo(opt)}
                        />
                     </Col>
                  </Row>

                  {reportingTestDates ? (
                     <Row className="mt-3">
                        <Col xs="3" className="centerRight">
                           Test Start Date:
                        </Col>
                        <Col xs="4">
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              options={reportingTestDates}
                              value={screenForm.payload.startDate}
                              onChange={(opt) => onChange("payload.startDate", opt)}
                           />
                        </Col>
                     </Row>
                  ): (false)}

                  {reportingTestDates ? (
                     <Row className="mt-3">
                        <Col xs="3" className="centerRight">
                           Test End Date:
                        </Col>
                        <Col xs="4">
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              options={reportingTestDates}
                              value={screenForm.payload.endDate}
                              onChange={(opt) => onChange("payload.endDate", opt)}
                           />
                        </Col>
                     </Row>
                  ): (false)}

                  <Row className="mt-3">
                     <Col xs="3" className="centerRight">
                        Confidence (%):
                     </Col>
                     <Col xs="4">
                        <Input
                           type="number"
                           step={1}
                           value={zLevel || ""}
                           onChange={(e) => handleZValChange(e.target.value)}
                        />
                     </Col>
                     <Col xs="3" className="centerLeft">
                        (for Excel output only)
                     </Col>
                  </Row>

                  {reportingTestDates ? (
                     <Row className="mt-3 mb-5">
                        <Col xs="3"></Col>
                        <Col xs="6" style={{ textAlign: "left", marginRight: "5px" }}>
                           <Row>
                              <Col xs={3}>
                                 <Button
                                    className="shredButton"
                                    onClick={TestingReport}
                                    disabled={!reportItem.selectedItem}
                                 >SHOW REPORT</Button>
                              </Col>
                              <Col xs={4} style={{ marginTop: ".4em" }} className="align-left">
                                 <Input type="radio"
                                    id="PDF"
                                    name="reportType"
                                    onChange={() => onChange("payload.isExcel", RadioButttonTypes.PDF)}
                                    className="me-2" />
                                 <label style={{ color: "white" }}>PDF</label>

                                 <Input type="radio"
                                    id="XLS"
                                    name="reportType"
                                    defaultChecked={true}
                                    onChange={() => onChange("payload.isExcel", RadioButttonTypes.XLS)}
                                    className="ms-4 me-2" />
                                 <label style={{ color: "white" }}>Excel</label>
                              </Col>
                              <Col>&nbsp;</Col>
                           </Row>
                        </Col>
                     </Row>
                  ) : (false)}
               </CardBody>
            </Card>
         ) : (false)}

         </DesktopContainer>
   );
}
